<script setup lang="ts">
import VarsityInlineConfirm from "@/components/VarsityInlineConfirm.vue"
import { createInlineConfirmCollection } from "@/composables/createInlineConfirm"
import type { ChampionshipRegistrationTeam } from "@/model/championship/championship-registration-team"
import type { RegistrationPermissions } from "@/pages/Tasks/Tasks/championship-registration"
import { useRegistrationPolicy } from "@/pages/Tasks/Tasks/useRegistrationPolicy"
import { defineTranslations } from "@/utils/defineTranslations"
import { $currency, $formatDate, createInlineTable, useHttpClient } from "@/vf"
import VfListCompose from "@/vf/components/crud/list/VfListCompose.vue"
import { computed } from "vue"
import ChampionshipRegistrationApprovalStateIcon from "./ChampionshipRegistrationApprovalStateIcon.vue"
import ChampionshipRegistrationEditTeamWizard from "./ChampionshipRegistrationEditTeamWizard.vue"
import ChampionshipRegistrationShowTeam from "./ChampionshipRegistrationShowTeam.vue"

const props = withDefaults(
    defineProps<{
        registrationId: string
        teams: ChampionshipRegistrationTeam[]
        showPrice?: boolean
        showEdit?: boolean
        showDelete?: boolean
        viewMode: "customer" | "admin"
        permissions?: RegistrationPermissions
    }>(),
    {
        showPrice: true,
        showEdit: false,
        showDelete: false,
    },
)
const emit = defineEmits<{
    (e: "updated"): void
}>()

const policy = useRegistrationPolicy(
    computed(() => props.permissions),
    computed(() => props.viewMode),
)

const inlineTable = createInlineTable({
    refresh: () => emit("updated"),
})

const buttons = computed<("show" | "edit" | "delete")[]>(() => {
    const btns: ("show" | "edit" | "delete")[] = ["show"]

    if (props.showEdit) {
        btns.push("edit")
    }

    if (props.showDelete) {
        btns.push("delete")
    }

    return btns
})

const colspan = computed(() => {
    let span = 6

    if (props.showPrice) {
        span++
    }

    if (buttons.value.length > 0) {
        span++
    }

    return span
})

const http = useHttpClient()
const deleteConfirms = createInlineConfirmCollection<{ id: string }>((id: string) => ({
    async action() {
        if (!policy.value.DeleteTeam) {
            return
        }

        await http.delete(`/championship/registration/${props.registrationId}/team/${id}?viewMode=${props.viewMode}`)
        emit("updated")
    },
}))

// Once a payment exists, teams can no longer be deleted; the delete button gives way to a cancel button
const showCancelInsteadOfDelete = computed(() => !policy.value.DeleteTeam && policy.value.CancelTeamRequest)
const cancelConfirms = createInlineConfirmCollection<{ id: string }>((id: string) => ({
    async action() {
        await http.post(
            `/championship/registration/${props.registrationId}/team/${id}/cancel-request?viewMode=${props.viewMode}`,
        )
        emit("updated")
    },
}))

const trans = defineTranslations({
    cancel_team: {
        de: "Team stornieren",
        en: "Cancel team",
    },
    cancel_confirmation: {
        de: "Nach einer Zahlung kann ein Team nicht mehr gelöscht, sondern nur noch storniert werden. Möchtest du dieses Team wirklich stornieren?",
        en: "Once a payment has been made, a team can no longer be deleted, only canceled. Do you really want to cancel this team?",
    },
    cancel_success: {
        de: "Das Team wurde storniert.",
        en: "The team was canceled.",
    },
})
</script>

<template>
    <table class="table table-striped">
        <thead>
            <tr>
                <th></th>
                <th>{{ $t("@tasks:tasks.championship_registration.team_list.team_name") }}</th>
                <th>{{ $t("@tasks:tasks.championship_registration.team_list.picture_approval") }}</th>
                <th>{{ $t("@tasks:tasks.championship_registration.team_list.category") }}</th>
                <th>{{ $t("@tasks:tasks.championship_registration.team_list.number_of_members") }}</th>
                <th>{{ $t("@tasks:tasks.championship_registration.team_list.contact_person") }}</th>
                <th v-if="showPrice">{{ $t("@tasks:tasks.championship_registration.team_list.total_price") }}</th>
                <th v-if="buttons.length > 0"></th>
            </tr>
        </thead>

        <tbody>
            <template v-for="team of props.teams" :key="team.id">
                <tr :class="{active: inlineTable.rowIsOpen(team.id!)}">
                    <td style="width: 1px" class="text-center pl-4">
                        <ChampionshipRegistrationApprovalStateIcon :state="team.approvalState" />
                    </td>
                    <td>
                        {{ team.teamName }}
                        <div
                            v-if="props.viewMode === 'admin' && team.waitingListApprovalAcceptedAt"
                            class="text-muted small"
                        >
                            {{
                                $t(
                                    "@tasks:tasks.championship_registration.waiting_list.waiting_list_approval_accepted_at",
                                    {
                                        date: $formatDate(team.waitingListApprovalAcceptedAt, "dd.MM.yyyy"),
                                        time: $formatDate(team.waitingListApprovalAcceptedAt, "HH:mm"),
                                    },
                                )
                            }}
                        </div>
                    </td>
                    <td>
                        <i
                            class="fa fa-fw"
                            :class="{
                                'fa-circle-check text-success': team.hasPictureApprovalForAllMembers,
                                'fa-times-circle text-danger': !team.hasPictureApprovalForAllMembers,
                            }"
                        ></i>
                    </td>
                    <td>
                        {{ team.championshipCategory?.name }}
                    </td>
                    <td>
                        <div>
                            {{
                                $t(
                                    "@tasks:tasks.championship_registration.team_list.number_of_participants",
                                    team.numberOfParticipants ?? 0,
                                    {
                                        named: {
                                            total: team.numberOfParticipants,
                                            named: team.numberOfNamedParticipants,
                                        },
                                    },
                                )
                            }}
                        </div>
                        <div>
                            {{
                                $t(
                                    "@tasks:tasks.championship_registration.team_list.number_of_alternates",
                                    team.numberOfAlternates ?? 0,
                                    {
                                        named: {
                                            total: team.numberOfAlternates,
                                            named: team.numberOfNamedAlternates,
                                        },
                                    },
                                )
                            }}
                        </div>
                        <div>
                            {{
                                $t(
                                    "@tasks:tasks.championship_registration.team_list.number_of_entourages",
                                    team.numberOfEntourages ?? 0,
                                    {
                                        named: {
                                            total: team.numberOfEntourages,
                                            named: team.numberOfNamedEntourages,
                                        },
                                    },
                                )
                            }}
                        </div>
                    </td>
                    <td>
                        <div v-if="team.safetyChampionData" class="mb-3">
                            <b>Safety Champion</b>
                            <div>
                                <i class="fa fa-fw fa-user"></i>
                                {{ team.safetyChampionData.name }}
                            </div>
                            <div>
                                <i class="fa fa-fw fa-envelope"></i>
                                {{ team.safetyChampionData.email }}
                            </div>
                        </div>
                        <div v-if="team.accuscoreMemberData">
                            <b>Accuscore</b>
                            <div>
                                <i class="fa fa-fw fa-user"></i>
                                {{ team.accuscoreMemberData.name }}
                            </div>
                            <div>
                                <i class="fa fa-fw fa-envelope"></i>
                                {{ team.accuscoreMemberData.email }}
                            </div>
                        </div>
                    </td>
                    <td v-if="showPrice">
                        {{ $currency(team.totalPrice) }}
                    </td>
                    <td class="text-right" v-if="props.registrationId">
                        <button
                            v-if="buttons.includes('show')"
                            type="button"
                            class="btn btn-primary"
                            @click="
                                inlineTable.open(
                                    { teamId: team.id, registrationId: props.registrationId },
                                    ChampionshipRegistrationShowTeam,
                                    team.id,
                                )
                            "
                        >
                            <i class="fa fa-fw fa-eye"></i>
                        </button>
                        <button
                            v-if="buttons.includes('edit')"
                            type="button"
                            class="btn btn-primary"
                            @click="
                                inlineTable.open(
                                    { team, registrationId },
                                    ChampionshipRegistrationEditTeamWizard,
                                    team.id,
                                )
                            "
                        >
                            <i class="fa fa-fw fa-edit"></i>
                        </button>
                        <button
                            v-if="buttons.includes('delete') && showCancelInsteadOfDelete"
                            type="button"
                            class="btn btn-danger"
                            :title="trans('cancel_team')"
                            :disabled="team.approvalState === 'canceled'"
                            @click="cancelConfirms(team.id!).toggle()"
                        >
                            <i class="fa fa-fw fa-ban"></i>
                        </button>
                        <button
                            v-else-if="buttons.includes('delete')"
                            type="button"
                            class="btn btn-danger"
                            :disabled="!policy.DeleteTeam"
                            @click="deleteConfirms(team.id!).toggle()"
                        >
                            <i class="fa fa-fw fa-trash"></i>
                        </button>
                    </td>
                </tr>
                <tr class="open-row">
                    <td :colspan="colspan" class="p-0">
                        <VarsityInlineConfirm :controller="deleteConfirms(team.id!)">
                            <template #confirmation>
                                {{ $t("@tasks:tasks.championship_registration.team_list.delete_confirmation") }}
                            </template>
                            <template #success>
                                {{ $t("@tasks:tasks.championship_registration.team_list.delete_success") }}
                            </template>
                        </VarsityInlineConfirm>
                        <VarsityInlineConfirm :controller="cancelConfirms(team.id!)">
                            <template #confirmation>
                                {{ trans("cancel_confirmation") }}
                            </template>
                            <template #success>
                                {{ trans("cancel_success") }}
                            </template>
                        </VarsityInlineConfirm>
                    </td>
                </tr>
                <VfListCompose :colspan="colspan" :row-id="team.id!" :inline-table="inlineTable"></VfListCompose>
                <slot name="secondaryRow" v-bind="{ team, colspan }"></slot>
            </template>
        </tbody>
    </table>
</template>

<style scoped>
tr.active > td {
    background-color: #e4edf6;
}
</style>
